import React, { useState, useEffect } from 'react';
import { Typography, Card, Row, Col, Statistic, Spin, Progress, Divider } from 'antd';
import { 
  ArrowUpOutlined, 
  ArrowDownOutlined,
  DollarOutlined,
  ShoppingCartOutlined,
  ShoppingOutlined,
  FileTextOutlined,
  WarningOutlined,
  AlertOutlined
} from '@ant-design/icons';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title as ChartTitle,
  Tooltip,
  Legend,
} from 'chart.js';
import api from '../api';
import { useNavigate } from 'react-router-dom';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ChartTitle,
  Tooltip,
  Legend
);

const { Title } = Typography;

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'top',
    },
    title: {
      display: false,
    },
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: {
        callback: function(value) {
          return '¥' + value.toLocaleString();
        }
      }
    },
  },
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [salesTrend, setSalesTrend] = useState(null);
  const [loading, setLoading] = useState(true);
  const [financialPeriod, setFinancialPeriod] = useState('today'); // 默认显示今日

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [statsRes, trendRes] = await Promise.all([
        api.get('/v1/dashboard/stats'),
        api.get('/v1/dashboard/sales-trend'),
      ]);
      setStats(statsRes.data);
      setSalesTrend(trendRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  const salesTrendData = salesTrend ? {
    labels: salesTrend.labels,
    datasets: salesTrend.datasets,
  } : null;

  // 根据选择的时间段计算财务数据
  const getFinancialData = (period) => {
    const salesAmount = stats?.sales?.[period] || 0;
    const purchaseAmount = stats?.purchase?.[period] || 0;
    const otherIncome = stats?.financial?.[`${period}_income`] || 0;
    const otherExpense = stats?.financial?.[`${period}_expense`] || 0;
    
    const totalIncome = salesAmount + otherIncome;
    const totalExpense = purchaseAmount + otherExpense;
    const netProfit = totalIncome - totalExpense;
    const profitMargin = totalIncome > 0 ? ((netProfit / totalIncome) * 100).toFixed(1) : 0;
    
    return { totalIncome, totalExpense, netProfit, profitMargin };
  };

  const financialData = getFinancialData(financialPeriod);
  
  const periodLabels = {
    today: '今日',
    week: '本周',
    month: '本月',
    year: '本年'
  };

  return (
    <div style={{ padding: '0 4px' }}>
      <Title level={3} style={{ marginBottom: 20, fontSize: '20px' }}>业务概览</Title>

      {/* 第一行：财务概览（4个卡片） */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        {/* 财务概览 */}
        <Col xs={24} sm={12} lg={6}>
          <Card 
            bordered={false}
            style={{ 
              height: '100%',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white'
            }}
            bodyStyle={{ padding: '16px' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <DollarOutlined style={{ fontSize: 18, marginRight: 8 }} />
                <span style={{ fontSize: 14, fontWeight: 500 }}>财务概览</span>
              </div>
            </div>
            
            <div style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: 8 }}>
                {['today', 'week', 'month', 'year'].map(period => (
                  <div
                    key={period}
                    onClick={() => setFinancialPeriod(period)}
                    style={{
                      padding: '2px 8px',
                      borderRadius: 3,
                      background: financialPeriod === period ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.1)',
                      cursor: 'pointer',
                      fontSize: 11,
                      fontWeight: financialPeriod === period ? 600 : 400,
                      transition: 'all 0.2s',
                      border: financialPeriod === period ? '1px solid rgba(255,255,255,0.5)' : '1px solid transparent'
                    }}
                  >
                    {periodLabels[period]}
                  </div>
                ))}
              </div>
            </div>
            
            <Row gutter={[8, 8]}>
              <Col span={12}>
                <div style={{ opacity: 0.9, fontSize: 11, marginBottom: 2 }}>总收入</div>
                <div style={{ fontSize: 18, fontWeight: 600 }}>¥{financialData.totalIncome.toLocaleString()}</div>
              </Col>
              
              <Col span={12}>
                <div style={{ opacity: 0.9, fontSize: 11, marginBottom: 2 }}>总支出</div>
                <div style={{ fontSize: 18, fontWeight: 600 }}>¥{financialData.totalExpense.toLocaleString()}</div>
              </Col>
              
              <Col span={12}>
                <div style={{ opacity: 0.9, fontSize: 11, marginBottom: 2 }}>净利润</div>
                <div style={{ fontSize: 18, fontWeight: 600, color: financialData.netProfit >= 0 ? '#52c41a' : '#ff4d4f' }}>
                  ¥{financialData.netProfit.toLocaleString()}
                </div>
              </Col>
              
              <Col span={12}>
                <div style={{ opacity: 0.9, fontSize: 11, marginBottom: 2 }}>利润率</div>
                <div style={{ fontSize: 18, fontWeight: 600 }}>{financialData.profitMargin}%</div>
              </Col>
            </Row>
          </Card>
        </Col>

        {/* 销售收入 */}
        <Col xs={24} sm={12} lg={6}>
          <Card 
            size="small" 
            bordered={false}
            style={{ height: '100%', cursor: 'pointer', transition: 'all 0.3s' }}
            bodyStyle={{ padding: '16px' }}
            onClick={() => navigate('/sales/sales-orders')}
            hoverable
          >
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ 
                width: 36, 
                height: 36, 
                borderRadius: 8, 
                background: '#e6f7ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: 10
              }}>
                <ShoppingCartOutlined style={{ fontSize: 18, color: '#1890ff' }} />
              </div>
              <span style={{ fontSize: 13, fontWeight: 500, color: '#262626' }}>销售收入</span>
            </div>
            <Statistic 
              value={stats?.sales?.month || 0} 
              precision={2} 
              prefix="¥"
              valueStyle={{ fontSize: 20, color: '#262626' }}
            />
            <Divider style={{ margin: '10px 0' }} />
            <Row gutter={[8, 8]}>
              <Col span={12}>
                <div style={{ fontSize: 11, color: '#8c8c8c' }}>今日</div>
                <div style={{ fontSize: 13, fontWeight: 500 }}>¥{(stats?.sales?.today || 0).toLocaleString()}</div>
              </Col>
              <Col span={12}>
                <div style={{ fontSize: 11, color: '#8c8c8c' }}>本周</div>
                <div style={{ fontSize: 13, fontWeight: 500 }}>¥{(stats?.sales?.week || 0).toLocaleString()}</div>
              </Col>
              <Col span={12}>
                <div style={{ fontSize: 11, color: '#8c8c8c' }}>本月</div>
                <div style={{ fontSize: 13, fontWeight: 500 }}>¥{(stats?.sales?.month || 0).toLocaleString()}</div>
              </Col>
              <Col span={12}>
                <div style={{ fontSize: 11, color: '#8c8c8c' }}>本年</div>
                <div style={{ fontSize: 13, fontWeight: 500 }}>¥{(stats?.sales?.year || 0).toLocaleString()}</div>
              </Col>
            </Row>
          </Card>
        </Col>

        {/* 采购支出 */}
        <Col xs={24} sm={12} lg={6}>
          <Card 
            size="small" 
            bordered={false}
            style={{ height: '100%', cursor: 'pointer', transition: 'all 0.3s' }}
            bodyStyle={{ padding: '16px' }}
            onClick={() => navigate('/procurement/purchase-orders')}
            hoverable
          >
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ 
                width: 36, 
                height: 36, 
                borderRadius: 8, 
                background: '#fff7e6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: 10
              }}>
                <ShoppingOutlined style={{ fontSize: 18, color: '#fa8c16' }} />
              </div>
              <span style={{ fontSize: 13, fontWeight: 500, color: '#262626' }}>采购支出</span>
            </div>
            <Statistic 
              value={stats?.purchase?.month || 0} 
              precision={2} 
              prefix="¥"
              valueStyle={{ fontSize: 20, color: '#262626' }}
            />
            <Divider style={{ margin: '10px 0' }} />
            <Row gutter={[8, 8]}>
              <Col span={12}>
                <div style={{ fontSize: 11, color: '#8c8c8c' }}>今日</div>
                <div style={{ fontSize: 13, fontWeight: 500 }}>¥{(stats?.purchase?.today || 0).toLocaleString()}</div>
              </Col>
              <Col span={12}>
                <div style={{ fontSize: 11, color: '#8c8c8c' }}>本周</div>
                <div style={{ fontSize: 13, fontWeight: 500 }}>¥{(stats?.purchase?.week || 0).toLocaleString()}</div>
              </Col>
              <Col span={12}>
                <div style={{ fontSize: 11, color: '#8c8c8c' }}>本月</div>
                <div style={{ fontSize: 13, fontWeight: 500 }}>¥{(stats?.purchase?.month || 0).toLocaleString()}</div>
              </Col>
              <Col span={12}>
                <div style={{ fontSize: 11, color: '#8c8c8c' }}>本年</div>
                <div style={{ fontSize: 13, fontWeight: 500 }}>¥{(stats?.purchase?.year || 0).toLocaleString()}</div>
              </Col>
            </Row>
          </Card>
        </Col>

        {/* 其他收支 */}
        <Col xs={24} sm={12} lg={6}>
          <Card 
            size="small" 
            bordered={false}
            style={{ height: '100%', cursor: 'pointer', transition: 'all 0.3s' }}
            bodyStyle={{ padding: '16px' }}
            onClick={() => navigate('/financial-records')}
            hoverable
          >
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ 
                width: 36, 
                height: 36, 
                borderRadius: 8, 
                background: '#f6ffed',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: 10
              }}>
                <FileTextOutlined style={{ fontSize: 18, color: '#52c41a' }} />
              </div>
              <span style={{ fontSize: 13, fontWeight: 500, color: '#262626' }}>其他收支</span>
            </div>
            <Row gutter={8} style={{ marginBottom: 10 }}>
              <Col span={12}>
                <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 2 }}>收入</div>
                <div style={{ fontSize: 16, fontWeight: 600, color: '#52c41a' }}>
                  ¥{(stats?.financial?.month_income || 0).toLocaleString()}
                </div>
              </Col>
              <Col span={12}>
                <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 2 }}>支出</div>
                <div style={{ fontSize: 16, fontWeight: 600, color: '#ff4d4f' }}>
                  ¥{(stats?.financial?.month_expense || 0).toLocaleString()}
                </div>
              </Col>
            </Row>
            <Divider style={{ margin: '10px 0' }} />
            <Row gutter={[8, 8]}>
              <Col span={12}>
                <div style={{ fontSize: 11, color: '#8c8c8c' }}>今日</div>
                <div style={{ fontSize: 11, fontWeight: 500 }}>
                  <span style={{ color: '#52c41a' }}>+{(stats?.financial?.today_income || 0).toLocaleString()}</span>
                  {' / '}
                  <span style={{ color: '#ff4d4f' }}>-{(stats?.financial?.today_expense || 0).toLocaleString()}</span>
                </div>
              </Col>
              <Col span={12}>
                <div style={{ fontSize: 11, color: '#8c8c8c' }}>本周</div>
                <div style={{ fontSize: 11, fontWeight: 500 }}>
                  <span style={{ color: '#52c41a' }}>+{(stats?.financial?.week_income || 0).toLocaleString()}</span>
                  {' / '}
                  <span style={{ color: '#ff4d4f' }}>-{(stats?.financial?.week_expense || 0).toLocaleString()}</span>
                </div>
              </Col>
              <Col span={12}>
                <div style={{ fontSize: 11, color: '#8c8c8c' }}>本月</div>
                <div style={{ fontSize: 11, fontWeight: 500 }}>
                  <span style={{ color: '#52c41a' }}>+{(stats?.financial?.month_income || 0).toLocaleString()}</span>
                  {' / '}
                  <span style={{ color: '#ff4d4f' }}>-{(stats?.financial?.month_expense || 0).toLocaleString()}</span>
                </div>
              </Col>
              <Col span={12}>
                <div style={{ fontSize: 11, color: '#8c8c8c' }}>本年</div>
                <div style={{ fontSize: 11, fontWeight: 500 }}>
                  <span style={{ color: '#52c41a' }}>+{(stats?.financial?.year_income || 0).toLocaleString()}</span>
                  {' / '}
                  <span style={{ color: '#ff4d4f' }}>-{(stats?.financial?.year_expense || 0).toLocaleString()}</span>
                </div>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {/* 第二行：库存预警 + 待办事项（3个卡片） */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        {/* 物料库存预警 */}
        <Col xs={24} lg={8}>
          <Card 
            title={
              <span style={{ fontSize: 14 }}>
                <AlertOutlined style={{ marginRight: 8, color: '#ff4d4f' }} />
                物料库存预警
              </span>
            }
            size="small" 
            bordered={false}
            bodyStyle={{ padding: '12px' }}
            style={{ height: '100%' }}
            extra={
              <span 
                style={{ fontSize: 12, color: '#1890ff', cursor: 'pointer' }}
                onClick={() => navigate('/procurement/materials', { state: { stockRange: '0-20' } })}
              >
                查看全部
              </span>
            }
          >
            {stats?.stock_alerts?.low_stock_materials?.length > 0 ? (
              <div style={{ maxHeight: '240px', overflowY: 'auto' }}>
                {stats.stock_alerts.low_stock_materials.slice(0, 3).map((material, index) => (
                  <div 
                    key={material.id}
                    style={{ 
                      padding: '10px 12px', 
                      background: index % 2 === 0 ? '#fafafa' : 'white',
                      borderRadius: 4,
                      marginBottom: 8,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onClick={() => navigate('/procurement/materials')}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 500, color: '#262626', marginBottom: 4 }}>
                        {material.name}
                      </div>
                      <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                        当前库存: <span style={{ color: material.current_stock < 10 ? '#ff4d4f' : '#fa8c16', fontWeight: 600 }}>
                          {material.current_stock}{material.unit}
                        </span>
                      </div>
                    </div>
                    <div style={{ 
                      padding: '4px 8px', 
                      background: material.current_stock < 10 ? '#fff1f0' : '#fff7e6',
                      color: material.current_stock < 10 ? '#ff4d4f' : '#fa8c16',
                      borderRadius: 4,
                      fontSize: 12,
                      fontWeight: 600,
                      marginLeft: 12,
                      whiteSpace: 'nowrap'
                    }}>
                      {material.current_stock < 10 ? '严重不足' : '库存偏低'}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px 0', color: '#8c8c8c' }}>
                <AlertOutlined style={{ fontSize: 32, marginBottom: 8, opacity: 0.3 }} />
                <div style={{ fontSize: 13 }}>暂无物料库存预警</div>
              </div>
            )}
          </Card>
        </Col>

        {/* 产品库存预警 */}
        <Col xs={24} lg={8}>
          <Card 
            title={
              <span style={{ fontSize: 14 }}>
                <AlertOutlined style={{ marginRight: 8, color: '#ff4d4f' }} />
                产品库存预警
              </span>
            }
            size="small" 
            bordered={false}
            bodyStyle={{ padding: '12px' }}
            style={{ height: '100%' }}
            extra={
              <span 
                style={{ fontSize: 12, color: '#1890ff', cursor: 'pointer' }}
                onClick={() => navigate('/sales/products', { state: { stockRange: '0-20' } })}
              >
                查看全部
              </span>
            }
          >
            {stats?.stock_alerts?.low_stock_products?.length > 0 ? (
              <div style={{ maxHeight: '240px', overflowY: 'auto' }}>
                {stats.stock_alerts.low_stock_products.slice(0, 3).map((product, index) => (
                  <div 
                    key={product.id}
                    style={{ 
                      padding: '10px 12px', 
                      background: index % 2 === 0 ? '#fafafa' : 'white',
                      borderRadius: 4,
                      marginBottom: 8,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onClick={() => navigate('/sales/products')}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 500, color: '#262626', marginBottom: 4 }}>
                        {product.name}
                      </div>
                      <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                        当前库存: <span style={{ color: product.current_stock < 10 ? '#ff4d4f' : '#fa8c16', fontWeight: 600 }}>
                          {product.current_stock}{product.unit}
                        </span>
                      </div>
                    </div>
                    <div style={{ 
                      padding: '4px 8px', 
                      background: product.current_stock < 10 ? '#fff1f0' : '#fff7e6',
                      color: product.current_stock < 10 ? '#ff4d4f' : '#fa8c16',
                      borderRadius: 4,
                      fontSize: 12,
                      fontWeight: 600,
                      marginLeft: 12,
                      whiteSpace: 'nowrap'
                    }}>
                      {product.current_stock < 10 ? '严重不足' : '库存偏低'}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px 0', color: '#8c8c8c' }}>
                <AlertOutlined style={{ fontSize: 32, marginBottom: 8, opacity: 0.3 }} />
                <div style={{ fontSize: 13 }}>暂无产品库存预警</div>
              </div>
            )}
          </Card>
        </Col>

        {/* 待办事项 */}
        <Col xs={24} lg={8}>
          <Card 
            title={
              <span style={{ fontSize: 14 }}>
                <WarningOutlined style={{ marginRight: 8, color: '#faad14' }} />
                待办事项
              </span>
            }
            size="small" 
            bordered={false}
            bodyStyle={{ padding: '12px' }}
            style={{ height: '100%' }}
          >
            <div 
              style={{ 
                padding: '12px', 
                background: '#fff7e6', 
                borderRadius: 6, 
                marginBottom: 12,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onClick={() => navigate('/sales/sales-orders', { state: { paymentStatus: 'pending' } })}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 14, color: '#595959' }}>待收款订单</span>
                <span style={{ fontSize: 20, fontWeight: 600, color: '#fa8c16' }}>
                  {stats?.financial?.outstanding_orders_count || 0}
                </span>
              </div>
              <div style={{ marginTop: 8, fontSize: 12, color: '#8c8c8c' }}>
                待收款金额: ¥{(stats?.financial?.outstanding_total_amount || 0).toLocaleString()}
              </div>
            </div>

            <div 
              style={{ 
                padding: '12px', 
                background: '#e6f7ff', 
                borderRadius: 6, 
                marginBottom: 12,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onClick={() => navigate('/sales/sales-orders')}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 14, color: '#595959' }}>待发货订单</span>
                <span style={{ fontSize: 20, fontWeight: 600, color: '#1890ff' }}>
                  {stats?.todo?.pending_sales_orders || 0}
                </span>
              </div>
            </div>

            <div 
              style={{ 
                padding: '12px', 
                background: '#f6ffed', 
                borderRadius: 6,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onClick={() => navigate('/procurement/purchase-orders')}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 14, color: '#595959' }}>待入库采购单</span>
                <span style={{ fontSize: 20, fontWeight: 600, color: '#52c41a' }}>
                  {stats?.todo?.pending_purchase_orders || 0}
                </span>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* 第三行：销售趋势图 */}
      <Row gutter={[12, 12]}>
        <Col xs={24}>
          <Card 
            title="近30天销售趋势" 
            size="small" 
            bordered={false}
            bodyStyle={{ padding: '16px' }}
          >
            <div style={{ height: '280px', position: 'relative' }}>
              {salesTrendData && <Line options={chartOptions} data={salesTrendData} />}
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;